import { store } from './store.js';
import { getConfig } from './config.js';
import { log_error_js } from './logging.js';
import { currentTimestamp } from "../helpers/time.js";

// Simple in-memory cache
const cache = new Map();
const retry_wait_time = 200; // ms
const server_errors = [500, 501, 502, 503, 504];
let is_refreshing_auth = false;
let refresh_auth_queue = [];

const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function validate(settings) {
    if (!settings || typeof settings !== 'object') throw new Error('No settings provided');
    if (!settings.url) throw new Error('No url provided');
}

function clean_settings(settings) {
    if (!settings.method && settings.type) {
        console.warn('Request: please provide method instead of type');
        settings.method = settings.type;
    }

    if (settings.cache_ttl === undefined) settings.cache_ttl = DEFAULT_CACHE_TTL;
    return settings;
}

function generate_url_search_params(data) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'object' && value !== null) {
            for (const [k2, v2] of Object.entries(value)) {
                if (typeof v2 === 'object' && v2 !== null) {
                    for (const [k3, v3] of Object.entries(v2)) {
                        params.append(`data[${key}][${k2}][${k3}]`, v3);
                    }
                } else {
                    params.append(`data[${key}][${k2}]`, v2);
                }
            }
        } else {
            params.append(`data[${key}]`, value);
        }
    }
    return params.toString();
}

function execute_success(settings, data, response = null) {
    if (typeof settings.success === 'function') {
        try {
            settings.success(data, response);
        } catch (error) {
            console.error('Success handler error:', error);
        }
    }
}

function execute_error(settings, errorData, statusCode, response = null) {
    if (typeof settings.error === 'function') {
        try {
            settings.error(errorData, statusCode, response);
        } catch (error) {
            console.error('Error handler error:', error);
        }
    }
}

function execute_complete(settings) {
    if (typeof settings.complete === 'function') {
        try {
            settings.complete();
        } catch (error) {
            console.error('Complete handler error:', error);
        }
    }
}

function process_refresh_auth_queue() {
    if (refresh_auth_queue.length === 0) return;
    const { settings, controller, attempt } = refresh_auth_queue.shift();
    handle_request(settings, controller, attempt);
}

function handle_auth_error(settings = null, controller = null, attempt = 1) {
    refresh_auth_queue.push({settings, controller, attempt});
    refresh_auth_token();
}

export function refresh_auth_token() {
    if (is_refreshing_auth) return;

    is_refreshing_auth = true;

    fetch(getConfig('api_url') + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: store.get('auth').refresh_token })
    })
        .then(res => {
            if (res.status !== 200) {
                throw new Error('Failed to refresh auth token. Response: ' + res.status);
            }

            return res.json().then(data => {
                data.auth.logged_in = true;
                store.set('auth', data.auth);
                store.set('user', data.user);
                store.set('services', data.services);

                is_refreshing_auth = false;
                process_refresh_auth_queue();
            })
        })
        .catch(err => {
            console.error('Error refreshing auth token:', err);
            log_error_js({
                type: 'error',
                message: 'Auth token refresh failed: ' + (err?.message || String(err)),
                stack: err?.stack || null,
                url: window.location.href,
                timestamp: currentTimestamp()
            });
            is_refreshing_auth = false;

            store.clear('auth');
            store.clear('user');
            store.clear('services');

            refresh_auth_queue = []

            // Redirect to login or handle logout
            window.history.pushState({}, "", "/login");
            window.dispatchEvent(new PopStateEvent('popstate'));
        });
}

function report_error(response, attempt=1) {
    if (attempt > 5) return;
    let data;

    if (!getConfig('is_prod')) {
        return;
    }

    if ([
        401,
        422,
    ].includes(response.status)) return;

    try {
        if (response && typeof response === 'object' && 'status' in response) {
            data = {
                status: response.status,
                url: response.url,
                // Don't use response.body directly, it's a stream.
                // Just send the status and URL for now to avoid complexity.
                timestamp: currentTimestamp(),
            };
        } else {
            data = {
                message: response.toString(),
                timestamp: currentTimestamp(),
            };
        }

        fetch(getConfig('api_url') + '/logging/error_api', {
            method: 'POST',
            headers: {
                'Accept': 'application/json; charset=UTF-8',
                'Content-Type': 'application/json; charset=UTF-8'
            },
            body: JSON.stringify(data),
        }).then(res => {
            if (res.ok) return; // Success!

            // If it's a 502, 503, or 504, it might be worth a retry
            const retryable_errors = [502, 503, 504];
            if (retryable_errors.includes(res.status) && attempt < 5) {
                setTimeout(() => report_error(response, attempt + 1), attempt * 500);
            } else {
                console.warn(`Logging to /errors failed with status: ${res.status}. Giving up.`);
            }
        }).catch(() => {
            setTimeout(() => report_error(response, attempt + 1), attempt * 300);
        });
    } catch (error) {
        console.warn('Error logging to /errors:', error);
    }
}

function parse_error_response(response) {
    return response.text().then(text => {
        try {
            return JSON.parse(text);
        } catch {
            return { errors: [text || 'Unknown Error'] };
        }
    });
}

function create_request(settings, controller) {
    const request = {
        headers: {
            'Accept': 'application/json; charset=UTF-8',
        },
        signal: controller.signal,
    };

    // If your store holds auth tokens, include them
    const auth = store.get("auth");
    const token = auth ? auth.token : null;
    if (token) {
        request['headers']['Authorization'] = `Bearer ${token}`;
    }

    const method = settings.method?.toUpperCase() || 'GET';
    if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        throw new Error(`Invalid method: ${method}`);
    }
    request.method = method;

    if (settings.data) {
        if (method === 'GET') {
            settings.full_url += (settings.full_url.includes('?') ? '&' : '?') + generate_url_search_params(settings.data);
        } else {
            request.headers['Content-Type'] = "application/json; charset=UTF-8";
            request.body = JSON.stringify(settings.data);
        }
    }

    if (!('retry' in settings)) settings.retry = true;

    return fetch(settings.full_url, request);
}

function handle_request(settings, controller, attempt = 1) {
    try {
        const request = create_request(settings, controller);

        request.then(response => {
            if (response.status === 401) {
                handle_auth_error(settings, controller, attempt);
                return;
            }
            if (response.status === 204) {
                execute_success(settings, {}, response);
                execute_complete(settings);
                return;
            }

            if (response.ok) {
                response.json()
                    .then(data => {
                        if (settings.method === 'GET' && settings.cache_ttl) {
                            cache.set(settings.url, {
                                data,
                                response,
                                expires: Date.now() + settings.cache_ttl,
                            });
                        }

                        execute_success(settings, data, response)
                    })
                    .catch(() => {
                        report_error(response);
                        execute_error(settings, { errors: ['Server Error'] }, response.status, response);
                    })
                    .finally(() => execute_complete(settings));
            } else {
                parse_error_response(response).then(errorData => {
                    if (settings.retry && server_errors.includes(response.status) && attempt < 5) {
                        setTimeout(() => {
                            handle_request(settings, controller, attempt + 1);
                        }, retry_wait_time * Math.pow(2, attempt));
                    } else {
                        report_error(response);
                        execute_error(settings, errorData, response.status, response);
                        execute_complete(settings);
                    }
                });
            }

        }).catch(error => {
            if (error.name === 'AbortError') return;

            if (settings.retry && attempt < 5) {
                setTimeout(() => {
                    handle_request(settings, controller,attempt + 1);
                }, retry_wait_time * Math.pow(2, attempt));
            } else {
                execute_error(settings, { errors: ['No Internet Connection'] }, null);
                execute_complete(settings);
            }
        });

    } catch (error) {
        console.error('Fatal request setup error (2):', error);
        report_error(error);
        execute_error(settings, {
            errors: ['Something went wrong.', 'Please refresh and try again.']
        }, null);
        execute_complete(settings);
    }
}

function request(settings, controller = null) {
    if (!controller) controller = new AbortController();

    try {
        validate(settings);
        settings = clean_settings(settings);
    } catch (error) {
        console.error('Fatal request setup error (1):', error);
        report_error(error);
        execute_error(settings, {
            errors: ['Something went wrong.', 'Please refresh and try again.']
        }, null);
        execute_complete(settings);
        return null;
    }

    // Cache GET requests
    settings.full_url = getConfig('api_url') + settings.url;
    if (settings.method === 'GET' && settings.cache_ttl && cache.has(settings.url)) {
        const { expires, data, response } = cache.get(settings.url);
        if (Date.now() < expires) {
            execute_success(settings, data, response);
            execute_complete(settings);
            return null;
        }
        cache.delete(settings.url); // expired
    }

    handle_request(settings, controller);

    return controller;
}

export const api = {
    get(settings, controller = null) {
        settings.method = 'GET';
        return request(settings, controller);
    },
    post(settings, controller = null) {
        settings.method = 'POST';
        return request(settings, controller);
    },
    put(settings, controller = null) {
        settings.method = 'PUT';
        return request(settings, controller);
    },
    delete(settings, controller = null) {
        settings.method = 'DELETE';
        return request(settings, controller);
    },
    clear_cache(url) {
        if (cache.has(url)) {
            cache.delete(url);
        }
    }
};
